import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect } from 'react';
import './PropertyWizard.css';

function SummaryStep({ onSubmit, initialData = {}, onPreviousStep, propertyId }) {
  const [name, setName] = useState(initialData.name || '');
  const [description, setDescription] = useState(initialData.description || '');
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    location: true,
    details: true,
    amenities: false,
    geoFeatures: false,
    plan: false,
    images: true,
    listing: true,
  });

  useEffect(() => {
    const fetchSummaryData = async () => {
      if (!propertyId) { setLoading(false); return; }
      try {
        setLoading(true);
        const token = localStorage.getItem('access_token');
        const response = await apiRequest(apiRoutes.propertyWizard.summary(propertyId), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Failed to fetch summary data');
        const data = await response.json();
        setSummaryData(data);
        if (data.property?.name) setName(data.property.name);
        if (data.property?.description) setDescription(data.property.description);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSummaryData();
  }, [propertyId]);

  const handleGenerateDescription = async () => {
    if (!propertyId) return;
    setAiGenerating(true);
    setAiError(null);
    try {
      const token = localStorage.getItem('access_token');
      const response = await apiRequest(apiRoutes.propertyWizard.summarise(propertyId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'خطا در تولید توضیحات');
      setDescription(data.description);
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      alert('لطفاً یک نام یا عنوان ملک را وارد کنید');
      return;
    }
    onSubmit({ name: name.trim(), description: description.trim() });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDate = (year) => year ? `${year} شمسی` : 'تعیین نشده';

  if (loading) {
    return (
      <div className="summary-step">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <div className="loading-text">در حال بارگذاری اطلاعات...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="summary-step">
        <div className="error-container">
          <div className="error-text">خطا در بارگذاری اطلاعات: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="summary-step-modern">
      {/* Step Header */}
      <div className="step-header">
        <h3>بررسی نهایی و تکمیل اطلاعات</h3>
        <p>لطفاً اطلاعات را بررسی کرده و نام و توضیحات ملک را تکمیل کنید</p>
      </div>

      {/* Property Name & Description Form */}
      <div className="property-essentials-card">
        <div className="essentials-header">
          <h4>اطلاعات ضروری ملک</h4>
          <span className="required-badge">الزامی</span>
        </div>

        <div className="form-group-compact">
          <label htmlFor="property-name">
            نام ملک <span className="required-star">*</span>
          </label>
          <input
            id="property-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="مثال: آپارتمان ۲ خوابه در خیابان ولیعصر"
            className="form-input-modern"
            required
          />
        </div>

        <div className="form-group-compact">
          <div className="description-label-row">
            <label htmlFor="property-description">توضیحات ملک</label>
            <button
              type="button"
              className="btn-ai-generate"
              onClick={handleGenerateDescription}
              disabled={aiGenerating || !propertyId}
              title="تولید خودکار توضیحات با هوش مصنوعی"
            >
              {aiGenerating ? (
                'در حال تولید...'
              ) : (
                'تولید با هوش مصنوعی'
              )}
            </button>
          </div>

          {aiError && (
            <div className="ai-error-message">{aiError}</div>
          )}

          <textarea
            id="property-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="توضیحات کامل درباره ملک، ویژگی‌ها، امکانات و... یا از دکمه هوش مصنوعی استفاده کنید"
            rows={4}
            className={`form-textarea-modern ${aiGenerating ? 'textarea-loading' : ''}`}
            disabled={aiGenerating}
          />

          {description && !aiGenerating && (
            <div className="ai-description-hint">
              می‌توانید متن را ویرایش کنید
            </div>
          )}
        </div>
      </div>

      {/* Compact Summary Sections */}
      {summaryData && (
        <div className="summary-sections-compact">

          {/* Location */}
          <div className="summary-card-compact">
            <div className="summary-card-header" onClick={() => toggleSection('location')}>
              <div className="header-left">
                <h5>موقعیت مکانی</h5>
              </div>
            </div>
            {expandedSections.location && (
              <div className="summary-card-body">
                <div className="info-row">
                  <span className="info-label">آدرس:</span>
                  <span className="info-value">{summaryData.property?.address || '—'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">کد پستی:</span>
                  <span className="info-value">{summaryData.property?.postal_code || '—'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Property Details */}
          <div className="summary-card-compact">
            <div className="summary-card-header" onClick={() => toggleSection('details')}>
              <div className="header-left">
                <h5>مشخصات ملک</h5>
              </div>
            </div>
            {expandedSections.details && (
              <div className="summary-card-body">
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">مساحت:</span>
                    <span className="info-value">
                      {summaryData.property?.area ? `${summaryData.property.area} م²` : '—'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">سال ساخت:</span>
                    <span className="info-value">{formatDate(summaryData.property?.establish_date)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">نوع:</span>
                    <span className="info-value">{summaryData.property?.property_type?.name || '—'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">کاربری:</span>
                    <span className="info-value">{summaryData.property?.usage?.name || '—'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Amenities */}
          {summaryData.amenities?.length > 0 && (
            <div className="summary-card-compact">
              <div className="summary-card-header" onClick={() => toggleSection('amenities')}>
                <div className="header-left">
                  <h5>امکانات ({summaryData.amenities.length})</h5>
                </div>
              </div>
              {expandedSections.amenities && (
                <div className="summary-card-body">
                  <div className="tags-container">
                    {summaryData.amenities.map((amenity, index) => (
                      <span key={index} className="tag-item">
                        {amenity.amenity?.description || amenity.amenity?.name}
                        {amenity.value ? `: ${amenity.value}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Listing & Price */}
          {summaryData.listing && (
            <div className="summary-card-compact">
              <div className="summary-card-header" onClick={() => toggleSection('listing')}>
                <div className="header-left">
                  <h5>
                    {summaryData.listing.listing_type === 'RENT' ? 'اطلاعات اجاره' : 'اطلاعات فروش'}
                    {summaryData.listing.listing_type_display
                      ? ` — ${summaryData.listing.listing_type_display}`
                      : ''}
                  </h5>
                </div>
              </div>
              {expandedSections.listing && (
                <div className="summary-card-body">
                  <div className="info-grid">
                    {summaryData.listing.listing_type === 'RENT' && summaryData.listing.rent_details && (() => {
                      const rd = summaryData.listing.rent_details;
                      return (
                        <>
                          {rd.monthly_rent != null && (
                            <div className="info-item">
                              <span className="info-label">اجاره ماهانه:</span>
                              <span className="info-value">{Number(rd.monthly_rent).toLocaleString('fa-IR')} تومان</span>
                            </div>
                          )}
                          {rd.min_deposit != null && (
                            <div className="info-item">
                              <span className="info-label">ودیعه (حداقل):</span>
                              <span className="info-value">{Number(rd.min_deposit).toLocaleString('fa-IR')} تومان</span>
                            </div>
                          )}
                          {rd.minRent != null && (
                            <div className="info-item">
                              <span className="info-label">اجاره ماهانه (حداقل):</span>
                              <span className="info-value">{Number(rd.minRent).toLocaleString('fa-IR')} تومان</span>
                            </div>
                          )}
                          {rd.lease_term_months != null && (
                            <div className="info-item">
                              <span className="info-label">مدت قرارداد:</span>
                              <span className="info-value">{rd.lease_term_months} ماه</span>
                            </div>
                          )}
                          {rd.furnished_status && (
                            <div className="info-item">
                              <span className="info-label">وضعیت مبله:</span>
                              <span className="info-value">{rd.furnished_status}</span>
                            </div>
                          )}
                          {rd.fair_price != null && (
                            <div className="info-item">
                              <span className="info-label">قیمت منصفانه:</span>
                              <span className="info-value">{Number(rd.fair_price).toLocaleString('fa-IR')} تومان</span>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {summaryData.listing.listing_type === 'SALE' && summaryData.listing.sale_details && (() => {
                      const sd = summaryData.listing.sale_details;
                      return (
                        <>
                          {sd.asking_price != null && (
                            <div className="info-item">
                              <span className="info-label">قیمت درخواستی:</span>
                              <span className="info-value">{Number(sd.asking_price).toLocaleString('fa-IR')} تومان</span>
                            </div>
                          )}
                          {sd.fair_price != null && (
                            <div className="info-item">
                              <span className="info-label">قیمت منصفانه:</span>
                              <span className="info-value">{Number(sd.fair_price).toLocaleString('fa-IR')} تومان</span>
                            </div>
                          )}
                          {sd.mortgage_status && sd.mortgage_status !== 'NONE' && (
                            <div className="info-item">
                              <span className="info-label">وام:</span>
                              <span className="info-value">{sd.mortgage_status}</span>
                            </div>
                          )}
                          {sd.mortgage_total_amount != null && (
                            <div className="info-item">
                              <span className="info-label">مبلغ وام:</span>
                              <span className="info-value">{Number(sd.mortgage_total_amount).toLocaleString('fa-IR')} تومان</span>
                            </div>
                          )}
                          {sd.mortgage_pay_per_month != null && (
                            <div className="info-item">
                              <span className="info-label">قسط ماهانه:</span>
                              <span className="info-value">{Number(sd.mortgage_pay_per_month).toLocaleString('fa-IR')} تومان</span>
                            </div>
                          )}
                          <div className="info-item">
                            <span className="info-label">مستأجر فعلی:</span>
                            <span className="info-value">{sd.has_tenant ? 'دارد' : 'ندارد'}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Geo Features */}
          {summaryData.geo_features?.length > 0 && (
            <div className="summary-card-compact">
              <div className="summary-card-header" onClick={() => toggleSection('geoFeatures')}>
                <div className="header-left">
                  <h5>ویژگی‌های مکانی ({summaryData.geo_features.length})</h5>
                </div>
              </div>
              {expandedSections.geoFeatures && (
                <div className="summary-card-body">
                  <div className="tags-container">
                    {summaryData.geo_features.map((feature, index) => (
                      <span key={index} className="tag-item geo-tag">
                        {feature.geo_feature?.description || feature.geo_feature?.name}
                        {feature.score && ` (${feature.score})`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Plan */}
          {summaryData.plan && (
            <div className="summary-card-compact">
              <div className="summary-card-header" onClick={() => toggleSection('plan')}>
                <div className="header-left">
                  <h5>پلان واحد</h5>
                </div>
              </div>
              {expandedSections.plan && (
                <div className="summary-card-body">
                  <div className="plan-preview-container">
                    <img src={summaryData.plan.plan_url} alt="پلان واحد" className="plan-preview-image" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Images */}
          {summaryData.images?.length > 0 && (
            <div className="summary-card-compact">
              <div className="summary-card-header" onClick={() => toggleSection('images')}>
                <div className="header-left">
                  <h5>تصاویر ({summaryData.images.length})</h5>
                </div>
              </div>
              {expandedSections.images && (
                <div className="summary-card-body">
                  <div className="images-gallery-compact">
                    {summaryData.images.map((image, index) => (
                      <div key={index} className="gallery-image-item">
                        <div className="image-wrapper">
                          <img
                            src={image.read_url || image.urls?.md || image.urls?.lg}
                            alt={image.alt_text || image.caption || `تصویر ${index + 1}`}
                            className="gallery-image"
                            onError={(e) => {
                              e.target.parentElement.innerHTML = '<div class="image-error">تصویر بارگذاری نشد</div>';
                            }}
                          />
                          {image.is_primary && (
                            <div className="primary-badge-compact">اصلی</div>
                          )}
                        </div>
                        {image.caption && (
                          <div className="image-caption-compact">{image.caption}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-buttons-modern">
        <button
          onClick={handleSubmit}
          className="btn-primary-modern"
          disabled={!name.trim()}
        >
          تأیید و ادامه
        </button>
        {onPreviousStep && (
          <button type="button" onClick={onPreviousStep} className="btn-secondary-modern">
            بازگشت
          </button>
        )}
      </div>
    </div>
  );
}

export default SummaryStep;
